import React, { useState, useRef } from "react";
import imgURL from "../../lib/imgURL";
import { connect } from "react-redux";
import { StatusWiseFooterText, getTicketStatusLabel } from "./Constants";

export interface TicketDetailItem {
    ticketId: string;
    ticketNumber: string | null;
    title: string;
    description: string;
    status: string;
    priority: string;
    createdAt: string;
    processStep?: string;
    ownerTeam?: string;
}

export interface TicketComment {
    commentId: string;
    userName: string;
    comment: string;
    createdDate: string;
    isAgent: boolean;
}

interface TicketDetailProps {
    ticket: TicketDetailItem;
    comments: TicketComment[];
    onBack: () => void;
    onAddComment: (comment: string) => void;
    userName: string;
}

const fmtFullDate = (iso: string): string => {
    const d = new Date(iso);
    const day = d.getDate();
    const ord =
        day % 10 === 1 && day !== 11 ? "st"
            : day % 10 === 2 && day !== 12 ? "nd"
                : day % 10 === 3 && day !== 13 ? "rd"
                    : "th";
    const month = d.toLocaleString("en-US", { month: "long" });
    const time = d.toLocaleString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    return `${day}${ord} ${month}, ${time}`;
};

const formatProcessStep = (step?: string): string => {
    if (!step) return "";
    return step.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStatusColor = (status: string): string => {
    const s = status.toLowerCase();
    if (s === "active" || s === "open" || s === "in_progress") return "#22c55e";
    if (s === "closed" || s === "resolved") return "#a1a1aa";
    if (s === "pending") return "#f59e0b";
    return "#a1a1aa";
};

const TicketDetail: React.FC<TicketDetailProps> = ({
    ticket,
    onBack,
}) => {
    const comment = [ticket?.description?.split("---Merchant Comment ---")?.[1]?.split("\n\n")?.[0]]?.filter(Boolean)?.[0];
    const slaText = StatusWiseFooterText[ticket?.status?.toLocaleLowerCase() as keyof typeof StatusWiseFooterText](ticket);
    return (
        <div className="chat-ticket-detail">
            {/* Header with back button */}
            <div className="chat-panel-header cursor-pointer" onClick={onBack}>
                <div className="chat-panel-header-back" >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#AEAEAE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15.833 10H4.167M9.167 15L4.167 10l5-5" />
                    </svg>
                </div>
                <div className="chat-panel-header-text">
                    <div className="chat-panel-header-title">Ticket #{ticket.ticketNumber || "N/A"}</div>
                </div>
                <div />{/* spacer */}
                <span className={`chat-ticket-status-dot chat-ticket-status-dot-${ticket.status.toLowerCase()}`} />
                {getTicketStatusLabel(ticket.status)}
            </div>

            {/* Body */}
            <div className="chat-panel-body">
                <div className="ticket-detail-card">
                    {/* Card Header */}
                    {slaText && <div className="ticket-detail-card-sla">
                        {slaText}
                    </div>}

                    {/* Info rows */}
                    <div className="ticket-detail-info">
                        <div className="ticket-detail-info-row">
                            <span className="ticket-detail-label">Category:</span>
                            <span className="ticket-detail-value">{ticket.title || "N/A"}</span>
                        </div>
                        <div className="ticket-detail-info-row">
                            <span className="ticket-detail-label">Step:</span>
                            <span className="ticket-detail-value">
                                {ticket.processStep ? formatProcessStep(ticket.processStep) : "N/A"}
                            </span>
                        </div>
                        <div className="ticket-detail-info-row">
                            <span className="ticket-detail-label">Description:</span>
                            <span className="ticket-detail-value" title={ticket?.description?.split("\n\n")?.[0] || "N/A"}  >{ticket?.description?.split("\n")?.[0] || "N/A"}</span>
                        </div>
                        <div className="ticket-detail-info-row">
                            <span className="ticket-detail-label">Timestamp:</span>
                            <span className="ticket-detail-value">
                                {ticket.createdAt ? fmtFullDate(ticket.createdAt) : "N/A"}
                            </span>
                        </div>
                    </div>

                    {/* Comments */}
                    <div className="ticket-detail-comments">
                        {comment && (
                            <>
                                <div className="ticket-detail-comments-title">Comments</div>
                                <div className="ticket-detail-comment-item">
                                    <div className="ticket-detail-comment-content">
                                        <textarea className="ticket-detail-comment-text" value={comment} readOnly />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    userName: state?.loginReducer?.loginState?.UserFullName,
});

export default connect(mapStateToProps)(TicketDetail);
